/**
 * The declared fixtures of this pay run — what is STILL a fixture after the roster
 * stopped being one.
 *
 * WHAT LEFT THIS FILE. `PAYROLL_EMPLOYEES` used to live here: a twelve-element array of
 * fictional Larson Family Farms employees. Every dollar BELOW gross was already real —
 * Payroll.tsx sent each employee's gross to the tested EasyFarm.PayrollDeductions engine
 * and rendered whatever came back — but the twelve people themselves existed nowhere in
 * the seven-table schema. They are rows now (`db/migrations/0003_employee.sql`, seeded by
 * `db/seed/0002_seed_demo.sql`), read over GET /api/demo/employees by
 * `hooks/usePayrollRoster.ts` and typed as `PayrollEmployee` in `api/types.ts` — which is
 * where the H-2A FICA determination and the open MN Paid Leave question are now
 * documented, because they travel with the field they describe.
 *
 * WHAT IS STILL A FIXTURE, and why it stayed here rather than following the roster into
 * the database: every constant below is a property of THIS DEMO RUN, not of a person.
 *
 * `PAY_PERIODS_PER_YEAR` = 26 (biweekly, matching "Pay period Aug 1–14") and the
 * `FilingStatus: "Single"` that usePayrollRun applies to every line are simplifying
 * defaults — a real payroll run reads each employee's actual W-4 elections and pay
 * schedule from their record, which the `employee` table does not carry and which
 * inventing would be worse than declaring. Flagged rather than hidden: see the note
 * rendered on the screen itself.
 */

export const PAY_PERIODS_PER_YEAR = 26;
export const TAX_YEAR = 2026;
/**
 * The fortnight this demo's figures are computed for.
 *
 * ⚠️ IT WAS NOT A FORTNIGHT. This read "Aug 1–15" until 2026-09-08 — fifteen days —
 * while the constant above declares 26 pay periods a year and its own comment claimed the
 * two matched. Fifteen days times twenty-six is a 390-day year. A fifteen-day period is
 * SEMI-MONTHLY, which is twenty-four periods, and the difference is not cosmetic: every
 * annual figure on the year-end screen is a per-period gross multiplied by 26.
 *
 * THE SERVER SETTLES IT, and it disagreed with this file. DemoEndpoints' filing calendar
 * anchors on "first Friday of the tax year, then every 14 days" — which is what produces
 * the "7 paydays in this quarter" the Form 941 card prints for Q1 and Q3, and 6 for Q2 and
 * Q4. A semi-monthly payroll is exactly six every quarter, so the screen was already
 * telling anyone who counted that this payroll is biweekly. Running that rule for 2026
 * gives a first payday of 2 January and a fortnight of **1–14 August**, paid Friday the
 * 14th. That is now what this says.
 *
 * Found by cross-reading the H-2A screen against the Form 941 card, 2026-09-08. Nobody had
 * put the label and the payday count side by side; a farmer or a bookkeeper would.
 *
 * ⚠️ IT IS A FIXTURE, AND THE SCREENS NOW SAY SO. Three headers rendered it as a plain fact —
 * "Pay period Aug 1–15 · 12 employees" — on `/payroll`, `/h2a` and `/paid-leave`. The trade
 * show is 2026-09-23, so on the day that reads as a pay period SEVEN WEEKS PAST, sitting above
 * figures that are otherwise all live and ledger-derived. A visitor asking "is this current?"
 * gets the wrong answer from the one fictional element on the screen.
 *
 * The date is NOT invented forward instead. The `employee` table carries no pay schedule (see
 * the note above), and manufacturing a fortnight that contains today would be exactly the kind
 * of plausible filler this file exists to refuse. The honest fix is to declare it where it is
 * read, which the word "Sample" does, rather than only here where nobody looks.
 */
export const PAY_PERIOD_LABEL = "Aug 1–14";

/**
 * Check date for this pay period. Drives BOTH the MN Paid Leave effective-date test
 * (the program starts 2026-01-01) and the reporting quarter the wage detail is built
 * for — 2026-08-14 is Q3. Sent to the API as data; the SERVER derives the quarter
 * through the engine's own WageDetailAggregator.QuarterOf rather than this app
 * deciding it.
 *
 * ⚠️ CORRECTED 2026-09-08, with PAY_PERIOD_LABEL above — it was 2026-08-15, a SATURDAY,
 * on a payroll the server pays on Fridays. Both dates are in Q3 and both are after the
 * program's start, so no figure moves; what changes is that the fixture now names a day
 * this payroll actually pays on.
 */
export const CHECK_DATE = "2026-08-14";

/**
 * The company's state, i.e. reginfo.STATE in the legacy app. Sent to the API, which
 * resolves it through the engine's StateProgramCatalog — an unconfigured state or year
 * is refused there rather than rated at a guessed percentage, so this string cannot
 * silently become the wrong program.
 *
 * Still a constant, and deliberately so for now: state belongs to the ENTITY in the
 * schema (see the tenancy note in `db/migrations/0001_initial.sql`), and it IS already
 * available over GET /api/demo/entities. Reading it from there is a real improvement and
 * a separate one — it changes which paid-leave program a run is rated under, which is a
 * compliance change, not a plumbing change.
 */
export const COMPANY_STATE = "MN";

/**
 * Biweekly periods in a calendar quarter, used ONLY to tell the reader how much of the
 * quarter the demo's single pay period actually represents. Not an input to any
 * calculation — see the disclosure rendered on the Paid Leave screen.
 */
export const BIWEEKLY_PERIODS_PER_QUARTER = 6;
